<template>
	<Col class="width-100 height-100">
		<Row class="flex-justify-space-between flex-align-center pt-7 pr-6">
			<Col>
				<h1 class="title-text">Issues</h1>
				<h3 class="description-text">Monitor your features - errors of the same type are grouped into issues</h3>
			</Col>
		</Row>
		<AppContentLayout class="dropdowns-list">
			<div class="lists-container scroller">
				<AppLoader class="mt-3" color="#101840" size="18px" v-if="showLoader" />
				<Col v-else class="pr-7">
					<div class="table-header-container width-100 display-flex flex-row flex-align-center flex-justify-space-between mb-2">
						<Row class="flex-align-center flex-col-9">
							<Row class="flex-align-center">
								<v-text-field
									v-if="searchSupported"
									outlined
									single-line
									hide-details
									v-model="searchString"
									@click:clear="onClear"
									ref="search_field_ref"
									@blur="isFocused = false"
									@focus="isFocused = true"
									@keydown.enter="onSearch"
									placeholder="Search feature"
									prepend-inner-icon="mdi-magnify"
									clear-icon="mdi-close-circle-outline"
									class="search-action-text-field width-100"
								>
									<template v-slot:append>
										<AnemicButton :class="{ focus: isFocused }" tile class="search-button" @click="onSearch">Search</AnemicButton>
									</template>
								</v-text-field>
							</Row>
						</Row>
						<Row class="flex-align-center flex-col-3">
							<Row class="mr-8 flex-align-center last-error-box">Last Error</Row>
							<Row class="flex-align-center">Error rate</Row>
						</Row>
					</div>
					<IssuesList v-on="$listeners" :isLoading="isLoading || showLoader" :issues="currentIssues" />
					<div v-intersect="endIntersect" v-if="hasNextPage" class="load-more-empty-space width-100 display-flex flex-centerize">
						<div class="width-100">
							<Loader small v-if="isLoading && currentPage > 1 && !showLoader" />
						</div>
					</div>
				</Col>
			</div>
		</AppContentLayout>
	</Col>
</template>
<script>
import { getIssues } from '@/store/modules/issues/actions';
import { useIssues } from '@/store/modules/issues/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import AppContentLayout from '@/views/AppLayout/AppContentLayout.vue';
import IssuesList from '@/views/IssuesView/IssuesList.vue';
export default {
	name: 'IssuesManagement',
	components: { AppContentLayout, IssuesList },
	computed: {
		...mapGettersShorthand({ issues: useIssues }),
		showLoader() {
			return !this.afterOneSecond || (this.isLoading && this.currentPage === 1);
		},
	},
	methods: {
		...mapActionsShorthand({ getIssues }),
		onSearch() {
			this.clearState();
			this.onMounted();
		},
		clearState() {
			this.currentPage = this.page;
			this.currentIssues = [];
			this.afterOneSecond = false;
		},
		onClear() {
			this.searchString = '';
			this.clearState();
			this.onMounted();
		},
		onMounted() {
			setTimeout(() => {
				this.afterOneSecond = true;
			}, 1500);
			this.loadIssues();
		},
		onScrollToBottom() {
			this.currentPage++;
			this.loadIssues(this.currentPage);
		},
		async loadIssues(page) {
			this.isLoading = true;
			if (page === 1) {
				this.lastUsedSearchString = this.searchString;
			}
			try {
				const issuesData = await this.getIssues({
					page,
					filterBy: { ...this.filterBy, error_message: this.lastUsedSearchString },
				});
				this.hasNextPage = Boolean(issuesData.next);
				this.currentIssues = [...this.currentIssues, ...issuesData.results];
			} catch (err) {
				console.error(err);
				this.$toast.error('Failed to load issues');
			} finally {
				this.isLoading = false;
			}
		},
		endIntersect(entries, observer, isIntersecting) {
			if (isIntersecting) {
				this.onScrollToBottom();
			}
		},
	},
	mounted() {
		this.onMounted();
	},
	data() {
		return {
			currentPage: 1,
			isLoading: false,
			isFocused: false,
			searchString: '',
			currentIssues: [],
			hasNextPage: false,
			afterOneSecond: false,
			lastUsedSearchString: '',
			searchSupported: false, // Will turn this on once we support searching through issues.
		};
	},
};
</script>

<style scoped lang="scss">
.load-more-empty-space {
	height: 100px;
}

.scroller {
	overflow: auto;
	color: #c1c1c1;
}
.title-text {
	font-size: 20px;
	letter-spacing: 0px;
	text-align: left;
	color: var(--primary-font-color);
}
.description-text {
	font-size: 14px;
	color: var(--Colors-Neutral-Text-colorTextSecondary, #646e87);
	font-weight: 300;
	text-align: left;
	letter-spacing: 0px;
}
.new-button {
	width: 170px !important;
	padding: 0px !important;
	height: 45px !important;
	border-radius: 8px !important;
	background-color: var(--fifth-color) !important;

	color: var(--bg-color);
	font-size: 14px !important;
	font-weight: normal !important;
}

.table-header-container {
	font-size: 12px;
	color: #9e9e9e;
	font-weight: 400;
}
.search-action-text-field {
	height: 35px !important;
	width: 385px !important;
	max-width: 385px;
	$re-margin-top: 6px;

	::v-deep {
		.v-input__prepend-inner {
			margin-top: $re-margin-top;
			font-size: 8px !important;
			height: 4px !important;
		}
		.v-icon {
			font-size: 16px !important;
		}
		.v-input__append-inner {
			cursor: pointer;
			margin-top: 0px;
			margin-right: -12px !important;

			&.focused {
				.search-button {
					border-color: #101840 !important;
					color: #101840 !important;
				}
			}
		}
	}
}
.search-button {
	height: 35px !important;
	font-size: 14px !important;
	padding: 0px 20px !important;
	color: #727375 !important;
	border-radius: 6px !important;
	background: white !important;
	font-weight: normal !important;
	letter-spacing: 0.21px !important;
	border: 1px solid #cbcbcb !important;
	border-radius: 0px 6px 6px 0px !important;
	&.focus {
		color: #5c8df5 !important;
		border-color: #5c8df5 !important;
	}
}
.last-error-box {
	width: 116px;
}
</style>
